import InfoIcon from "@mui/icons-material/Info";
import { Tooltip } from "@mui/material";
import { StatusTagChip } from "components/UI/StatusTags";
import { observer } from "mobx-react";
import React from "react";
import {
    ApplicationFilterItemsStore,
    type FilterKey,
} from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import { useStore } from "utils/useStore";

interface Props {
    /**
     * The Application Filter behind this entry, from its `ConversationFilter`. Optional only
     * because entries saved to localStorage before the field existed have none; those render as a
     * bare title.
     */
    filterKey?: FilterKey;
    children: React.ReactNode;
}

/**
 * A Conversations drawer filter's title, followed by the status tag and info text its Application
 * Filter declares.
 *
 * Reached through the entry's own `filterKey`, because this drawer identifies filters by
 * `storeObjectKey` and that is not the Application Filter key.
 *
 * Shared by the filter list and the Manage Filters drawer rather than written per surface. Two
 * Theme filters are titled the same in both — matching the Signals filter picker — and the tag is
 * the only thing telling them apart, so a surface that stated status its own way could disagree
 * with the one next to it, or quietly render no tag at all and leave the pair indistinguishable.
 *
 * Renders the title alone when the filter declares neither, so callers do not have to check.
 */
export const FilterTitleWithStatus = observer(
    ({ filterKey, children }: Props) => {
        const applicationFilterItemsStore = useStore(
            ApplicationFilterItemsStore,
        );

        const item = filterKey
            ? applicationFilterItemsStore.getFilterItem(filterKey)
            : undefined;

        if (!item?.status && !item?.infoText) return <>{children}</>;

        return (
            <>
                {children} {item.status && <StatusTagChip {...item.status} />}
                {item.infoText && (
                    <Tooltip title={item.infoText} arrow>
                        <InfoIcon color="disabled" fontSize="small" />
                    </Tooltip>
                )}
            </>
        );
    },
);

export default FilterTitleWithStatus;
